import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import authService from '@/services/authService'
import adminUsersApiService from '@/services/adminUsersApiService'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(localStorage.getItem('admin_token') || null)
  const loading = ref(false)
  const error = ref(null)

  // Impersonation state
  const isImpersonating = ref(localStorage.getItem('is_impersonating') === 'true')
  const originalToken = ref(localStorage.getItem('original_admin_token') || null)
  const originalUser = ref(JSON.parse(localStorage.getItem('original_admin_user') || 'null'))
  const impersonatedBy = ref(JSON.parse(localStorage.getItem('impersonated_by') || 'null'))

  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const currentUser = computed(() => user.value)
  const userRole = computed(() => user.value?.role?.slug || null)
  const userPermissions = computed(() => user.value?.role?.permissions || [])

  // Role checks
  const isSuperAdmin = computed(() => userRole.value === 'superadmin')
  const isAdmin = computed(() => userRole.value === 'admin')
  const isSupport = computed(() => userRole.value === 'support')

  // Permission checks
  // ⚠️ SECURITY NOTE: These functions are for UX only (hiding UI elements)!
  // Always enforce permissions server-side via Laravel middleware.
  // Client-side checks can be bypassed - they are NOT a security measure.
  const hasPermission = (permissionSlug) => {
    if (isSuperAdmin.value) return true // Superadmin has all permissions
    return userPermissions.value?.some(p => p.slug === permissionSlug) || false
  }

  const hasAnyPermission = (permissionSlugs) => {
    if (isSuperAdmin.value) return true
    return permissionSlugs.some(slug => hasPermission(slug))
  }

  const hasAllPermissions = (permissionSlugs) => {
    if (isSuperAdmin.value) return true
    return permissionSlugs.every(slug => hasPermission(slug))
  }

  // Actions
  async function login(email, password) {
    loading.value = true
    error.value = null

    try {
      const response = await authService.login(email, password)

      token.value = response.data.token
      user.value = response.data.user

      localStorage.setItem('admin_token', response.data.token)

      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Login failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    loading.value = true
    error.value = null

    try {
      await authService.logout()
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      token.value = null
      user.value = null
      localStorage.removeItem('admin_token')
      loading.value = false
    }
  }

  async function fetchUser() {
    if (!token.value) return

    loading.value = true
    error.value = null

    try {
      const response = await authService.getCurrentUser()
      user.value = response.data.user
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to fetch user'
      // If unauthorized, clear auth state
      if (err.response?.status === 401) {
        await logout()
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  async function forgotPassword(email) {
    loading.value = true
    error.value = null

    try {
      const response = await authService.forgotPassword(email)
      return response
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to send reset link'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function resetPassword(email, password, password_confirmation, token) {
    loading.value = true
    error.value = null

    try {
      const response = await authService.resetPassword(email, password, password_confirmation, token)
      return response
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to reset password'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function startImpersonation(targetAdminUserId) {
    loading.value = true
    error.value = null

    try {
      // Generate impersonation token
      const generateResponse = await adminUsersApiService.generateImpersonationToken(targetAdminUserId)

      if (!generateResponse.data.success) {
        throw new Error(generateResponse.data.message || 'Failed to generate impersonation token')
      }

      const impersonationToken = generateResponse.data.data.token

      // Validate token and get auth token
      const validateResponse = await adminUsersApiService.validateImpersonationToken(impersonationToken)

      if (!validateResponse.data.success) {
        throw new Error(validateResponse.data.message || 'Failed to validate impersonation token')
      }

      const { user: targetUser, token: newToken, impersonated_by: impersonator } = validateResponse.data.data

      // Store original session
      localStorage.setItem('original_admin_token', token.value)
      localStorage.setItem('original_admin_user', JSON.stringify(user.value))
      localStorage.setItem('is_impersonating', 'true')
      localStorage.setItem('impersonated_by', JSON.stringify(impersonator))

      originalToken.value = token.value
      originalUser.value = user.value
      isImpersonating.value = true
      impersonatedBy.value = impersonator

      // Switch to impersonated user
      token.value = newToken
      user.value = targetUser
      localStorage.setItem('admin_token', newToken)

      return validateResponse.data
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Failed to impersonate user'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function stopImpersonation() {
    loading.value = true
    error.value = null

    try {
      // Try to end impersonation on server (revoke current token)
      try {
        await adminUsersApiService.endImpersonation()
      } catch (err) {
        console.error('Error ending impersonation on server:', err)
      }

      // Restore original session
      token.value = originalToken.value
      user.value = originalUser.value
      localStorage.setItem('admin_token', originalToken.value)

      // Clear impersonation state
      isImpersonating.value = false
      originalToken.value = null
      originalUser.value = null
      impersonatedBy.value = null

      localStorage.removeItem('original_admin_token')
      localStorage.removeItem('original_admin_user')
      localStorage.removeItem('is_impersonating')
      localStorage.removeItem('impersonated_by')

      // Refresh user data
      await fetchUser()

      return true
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to stop impersonation'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    // Impersonation state
    isImpersonating,
    originalUser,
    impersonatedBy,
    // Getters
    isAuthenticated,
    currentUser,
    userRole,
    userPermissions,
    isSuperAdmin,
    isAdmin,
    isSupport,
    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    // Actions
    login,
    logout,
    fetchUser,
    forgotPassword,
    resetPassword,
    startImpersonation,
    stopImpersonation,
  }
})
